/**
 * Talking to the food database, through the same-origin proxy.
 *
 * Entirely optional. It never throws, never blocks, and a failure leaves you typing
 * macros in by hand exactly as before. The app has to work in a gym with no signal,
 * so a lookup is a convenience and never a dependency.
 */
import type { FoodResult } from '../plan/foodLookup.ts';

export const FOOD_ENDPOINT = '/api/food';
const TIMEOUT_MS = 9000;

export interface SearchOutcome {
  ok: boolean;
  results: FoodResult[];
  /** Verbatim, for display. Same honesty rule as everywhere else. */
  error: string | null;
  note: string | null;
}

const fail = (error: string): SearchOutcome => ({ ok: false, results: [], error, note: null });

async function call(params: Record<string, string>): Promise<SearchOutcome> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return fail('You are offline. Foods you have used before are still listed below.');
  }
  const url = `${FOOD_ENDPOINT}?${new URLSearchParams(params).toString()}`;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch(url, { signal: ctrl.signal });
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return fail(/abort/i.test(msg) ? 'The search took too long. Try again, or type the macros in.'
      : `Could not reach the food database: ${msg}`);
  } finally { clearTimeout(timer); }

  const text = await res.text().catch(() => '');
  let body: { ok?: boolean; results?: FoodResult[]; error?: string; note?: string };
  try { body = JSON.parse(text); }
  catch { return fail(`The food database answered with something that was not JSON (HTTP ${res.status}).`); }

  if (!res.ok || body.ok !== true) {
    return fail(typeof body.error === 'string' ? body.error : `HTTP ${res.status}`);
  }
  return { ok: true, results: Array.isArray(body.results) ? body.results : [],
           error: null, note: typeof body.note === 'string' ? body.note : null };
}

export function searchFoods(q: string): Promise<SearchOutcome> {
  const term = q.trim();
  if (term === '') return Promise.resolve(fail('Type something to search for.'));
  return call({ q: term });
}

export function lookupBarcode(code: string): Promise<SearchOutcome> {
  const c = code.trim();
  if (!/^\d{6,14}$/.test(c)) return Promise.resolve(fail('A barcode is 6 to 14 digits.'));
  return call({ code: c });
}
